// The dashboard's commands: its cursor over rows, stacks and runs
// (logic/dashFocus.ts says where it rests and moves), and what opens,
// folds and refreshes.

import { navigate, stopAction, type CursorCommand, type Stop } from '../logic/dashFocus';
import { times, type Command } from './command';
import { leave, nothingFor, pass, type Update } from './effects';

/** The dashboard's own model: where its cursor was last put. */
export interface DashState {
  cursor: string | null;
}

/** What the dashboard shows and does not own, as it is when a message is sent. */
export interface DashEnv {
  /** The places the cursor rests, top to bottom. */
  stops: readonly Stop[];
  /** The stop whose row has the focus; null when the focus is elsewhere. */
  focused: string | null;
  refreshing: boolean;
}

/** A command, or the cursor put on a stop outright (the focus went there, or its row went away). */
export type DashMsg = Command | { kind: 'rest'; key: string | null };

/** The two together, as the update works on them: `cursor` is the stop the keys work from (the first, while the one last put is not shown). */
type DashModel = DashEnv & { cursor: string | null };

/** The stop the keys work from: the cursor's, or the first while the cursor's is not shown. */
export const currentStop = (state: DashState, stops: readonly Stop[]): string | null => (stops.some((s) => s.key === state.cursor) ? state.cursor : (stops[0]?.key ?? null));

export type DashEffect =
  /** Put the focus on the stop and show its row. */
  | { kind: 'focus'; key: string | null }
  /** Fold or unfold the stack or run the stop heads. */
  | { kind: 'toggle'; key: string }
  /** Open the pull request a stack starts at. */
  | { kind: 'open-start'; key: string }
  /** Work the focused row's own control (its link or button). */
  | { kind: 'activate' }
  | { kind: 'refresh' }
  | { kind: 'focus-filter' }
  | { kind: 'show-guide' };

/** Whether the dashboard has anything `command` could act on: what its key guide lists. */
export function dashboardOffers(command: Command): boolean {
  switch (command.kind) {
    case 'move':
      return (command.object === 'pr' || command.object === 'group') && !command.only;
    case 'act':
      return command.verb === 'open';
    case 'fold':
      return !command.object && !command.level;
    case 'goto':
      return command.place === 'top' || command.place === 'end';
    case 'open':
      return command.menu !== 'stack';
    case 'refresh':
      return true;
    case 'scroll':
    case 'select':
    case 'range':
    case 'grow':
    case 'present':
    case 'submit':
    case 'cancel':
      return false;
  }
}

/** The cursor command a command names here, or null. */
function cursorCommand(command: Command, at: Stop): CursorCommand | null {
  switch (command.kind) {
    case 'move':
      return { kind: command.object === 'group' ? 'group' : 'move', delta: command.dir };
    case 'fold':
      // A toggle unfolds what is folded at the cursor, and folds anything else.
      return { kind: 'fold', open: command.to === 'toggle' ? at.open === false : command.to === 'open' };
    case 'act':
      return { kind: 'open' };
    default:
      return null;
  }
}

/** A command for the cursor: from elsewhere on the page it only brings the focus back, so none acts on a row out of sight. */
function updateCursor(m: DashModel, command: Extract<Command, { kind: 'move' | 'fold' | 'act' }>): Update<DashModel, DashEffect> {
  // With nothing to act on, the key keeps its default (the arrows scroll the page).
  const stop = m.stops.find((s) => s.key === (m.focused ?? m.cursor));
  if (!stop) return [m, [leave]];
  const step = cursorCommand(command, stop)!;
  switch (stopAction(stop, step, m.focused !== null)) {
    case 'focus':
      return [m, [{ kind: 'focus', key: m.cursor }]];
    case 'start':
      return [m, [{ kind: 'open-start', key: stop.key }]];
    case 'control':
      return [m, [{ kind: 'activate' }]];
    case 'navigate': {
      if (step.kind === 'open') return [m, []];
      // A count repeats a move; a fold happens once (the stops change under it).
      let key = stop.key;
      let toggle: string | null = null;
      for (let n = step.kind === 'fold' ? 1 : times(command as { count?: number }); n > 0; n--) {
        const next = navigate(m.stops, key, step);
        toggle = next.toggle;
        if (next.key === null || next.key === key) break;
        key = next.key;
      }
      return [{ ...m, cursor: key }, [...(toggle ? [{ kind: 'toggle', key: toggle } as const] : []), { kind: 'focus', key }]];
    }
  }
}

/** The dashboard's update: the state that comes back is its model (the same object when the cursor did not move). */
export function updateDashboard(state: DashState, env: DashEnv, msg: DashMsg): Update<DashState, DashEffect> {
  if (msg.kind === 'rest') return [msg.key === state.cursor ? state : { cursor: msg.key }, []];
  const cursor = currentStop(state, env.stops);
  const [next, effects] = step({ ...env, cursor }, msg);
  return [next.cursor === cursor ? state : { cursor: next.cursor }, effects];
}

function step(m: DashModel, command: Command): Update<DashModel, DashEffect> {
  const here = 'on the dashboard';
  switch (command.kind) {
    case 'refresh':
      return [m, m.refreshing ? [] : [{ kind: 'refresh' }]];
    case 'open':
      if (command.menu === 'guide') return [m, [{ kind: 'show-guide' }]];
      if (command.menu === 'finder') return [m, [{ kind: 'focus-filter' }]];
      if (command.menu === 'stack') return [m, [nothingFor(command, here)]];
      return [m, [pass]];
    case 'goto': {
      if (command.place !== 'top' && command.place !== 'end') return [m, [nothingFor(command, here)]];
      const to = command.place === 'top' ? m.stops[0] : m.stops[m.stops.length - 1];
      return to ? [{ ...m, cursor: to.key }, [{ kind: 'focus', key: to.key }]] : [m, [leave]];
    }
    case 'move':
    case 'fold':
    case 'act':
      if (dashboardOffers(command)) return updateCursor(m, command);
      return [m, [nothingFor(command, here)]];
    case 'scroll':
    case 'select':
    case 'range':
    case 'grow':
    case 'present':
      return [m, [nothingFor(command, here)]];
    case 'submit':
    case 'cancel':
      return [m, [pass]];
  }
}
